import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Milk, IndianRupee, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { TabbedPage } from '../features/common/TabbedPage';
import { ILMSCard } from '../components/ILMSCard';
import { ILMSKPI } from '../components/ILMSKPI';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSDatePicker } from '../components/ILMSDatePicker';
import { ILMSSelect } from '../components/ILMSForm';
import { ILMSCardSkeleton } from '../components/ILMSLoadingState';
import { reportsApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';
import { formatCurrency, formatDate, formatNumber, today } from '../utils/format';
import { useReferenceData } from '../features/common/referenceData';
import type { MilkReportGroup } from '../types';
import { SmartChart } from '../features/charts/SmartChart';
import { cn } from '../utils/cn';

// "2026-10" -> "October 2026" — used by both reports' Monthly Summary tables.
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// "2026-10" + 1 -> "2026-11" (and -1 -> "2026-09") — the calendar's month nav.
function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Drops the run of zero-production entries BEFORE the first real one (on
// request) - a default 30/90-day range very often starts well before real
// data exists, which otherwise draws a long empty stretch (chart and table
// both) ahead of anything worth looking at. An entry AFTER real data has
// started stays visible even if it's zero (a genuinely missed day/month) -
// only the leading gap is cut.
function trimLeadingEmpty<T extends { total_litres: number }>(entries: T[]): T[] {
  const firstIndex = entries.findIndex((e) => e.total_litres > 0);
  return firstIndex <= 0 ? entries : entries.slice(firstIndex);
}

const RANGE_PRESETS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const SHIFT_GROUPS = [
  { key: 'morning' as const, label: 'Morning', bg: 'bg-warning-50', barColor: '#2f6335' },
  { key: 'evening' as const, label: 'Evening', bg: 'bg-info-50', barColor: '#8fbd91' },
];

function dayFat(shifts: { litres: number | string; fat_percent?: number | string | null }[]): number | null {
  let litres = 0;
  let fatLitres = 0;
  for (const s of shifts) {
    const l = Number(s.litres || 0);
    if (s.fat_percent == null || !l) continue;
    litres += l;
    fatLitres += l * Number(s.fat_percent);
  }
  return litres ? Math.round((fatLitres / litres) * 100) / 100 : null;
}

function DailyMilkProductionReport() {
  const [startDate, setStartDate] = useState(daysAgo(29));
  const [endDate, setEndDate] = useState(today());
  const [animalId, setAnimalId] = useState('');
  const { data: refData } = useReferenceData(['animals']);
  const reportQuery = useQuery({
    queryKey: ['report-milk-production', startDate, endDate, animalId],
    queryFn: () => reportsApi.milkProduction({ startDate, endDate, animalId: animalId || undefined }),
  });
  const report = reportQuery.data;
  const showThird = report?.days.some((d) => d.third.litres > 0) ?? false;
  // Each day's own Fat % - every shift's fat weighted by its litres.
  const chartDays = report ? trimLeadingEmpty(report.days).map((d) => ({ ...d, day_fat_percent: dayFat([d.morning, d.evening, d.third]) })) : [];
  const tableMonths = report ? trimLeadingEmpty(report.months) : [];

  return (
    <ILMSCard
      title="Daily Milk Production"
      subtitle="Litres, Fat %, Rate and Amount by Morning/Evening shift — mirrors the collection hub slip, with a cumulative total that resets fresh on the 1st of each month."
    >
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <ILMSDatePicker label="From" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        <ILMSDatePicker label="To" value={endDate} max={today()} onChange={(e) => setEndDate(e.target.value)} />
        <div className="flex flex-col gap-1">
          <label className="text-label text-ink-700">Animal</label>
          <ILMSSelect value={animalId} onChange={(e) => setAnimalId(e.target.value)} className="min-w-[220px]">
            <option value="">All animals (farm-wide)</option>
            {refData.animals.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </ILMSSelect>
        </div>
        <div className="flex gap-1.5 pb-0.5">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => { setEndDate(today()); setStartDate(daysAgo(p.days - 1)); }}
              className="rounded-md border border-ink-300/30 px-2.5 h-9 text-helper-text text-ink-600 hover:border-brand-green-400 hover:text-brand-green-700"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {reportQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(reportQuery.error)}</ILMSAlert>}
      {reportQuery.isLoading && <ILMSCardSkeleton count={1} />}

      {report && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            <ILMSKPI label="Total Litres" value={`${formatNumber(report.total_litres, 1)} L`} icon={<Milk />} tone="success" />
            <ILMSKPI label="Total Amount (₹)" value={formatCurrency(report.total_amount)} icon={<IndianRupee />} tone="info" />
            <ILMSKPI label="Morning Fat %" value={report.morning_fat_percent != null ? formatNumber(report.morning_fat_percent, 2) : '—'} helperText="Litres-weighted" icon={<Milk />} tone="neutral" />
            <ILMSKPI label="Evening Fat %" value={report.evening_fat_percent != null ? formatNumber(report.evening_fat_percent, 2) : '—'} helperText="Litres-weighted" icon={<Milk />} tone="neutral" />
            <ILMSKPI
              label="Peak Day"
              value={report.best_day ? `${formatNumber(report.best_day.total_litres, 1)} L` : '—'}
              helperText={report.best_day ? formatDate(report.best_day.production_date) : 'No production in range'}
              icon={<TrendingUp />}
              tone="success"
            />
            <ILMSKPI
              label="Lowest Day"
              value={report.worst_day ? `${formatNumber(report.worst_day.total_litres, 1)} L` : '—'}
              helperText={report.worst_day ? formatDate(report.worst_day.production_date) : 'No production in range'}
              icon={<TrendingDown />}
              tone="warning"
            />
          </div>

          {report.days.every((d) => d.total_litres === 0) ? (
            <p className="text-secondary text-ink-400 py-10 text-center">No milk sessions recorded in this range.</p>
          ) : (
            <SmartChart id="reports-milk" data={chartDays} />
          )}
          {report.days.some((d) => d.morning.fat_percent != null || d.evening.fat_percent != null) && (
            <div className="mt-4">
              <SmartChart id="reports-fat" data={chartDays} />
            </div>
          )}

          {tableMonths.length > 1 && (
            <div className="mt-6">
              <p className="text-card-title mb-2">Monthly Summary</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-table-text">
                  <thead>
                    <tr>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-left">Month</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Days with Production</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Sessions</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Litres</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Morning Fat %</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Evening Fat %</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableMonths.map((m) => (
                      <tr key={m.month}>
                        <td className="border border-ink-300/20 px-2 py-1.5">{monthLabel(m.month)}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{m.days_with_production}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{m.session_count}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{formatNumber(m.total_litres, 1)}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{m.morning_fat_percent != null ? formatNumber(m.morning_fat_percent, 2) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{m.evening_fat_percent != null ? formatNumber(m.evening_fat_percent, 2) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-medium">{formatCurrency(m.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 overflow-x-auto">
            <p className="text-card-title mb-2">Daily Detail</p>
            <table className="w-full border-collapse text-table-text">
              <thead>
                <tr>
                  <th rowSpan={2} className="border border-ink-300/20 px-2 py-1.5 align-bottom text-left">Date</th>
                  {SHIFT_GROUPS.map((g) => (
                    <th key={g.key} colSpan={5} className={`border border-ink-300/20 px-2 py-1.5 text-center font-semibold ${g.bg}`}>{g.label}</th>
                  ))}
                  {showThird && <th colSpan={5} className="border border-ink-300/20 px-2 py-1.5 text-center font-semibold bg-surface-muted">Third</th>}
                  <th rowSpan={2} className="border border-ink-300/20 px-2 py-1.5 align-bottom text-right">Cows<br />(day)</th>
                  <th rowSpan={2} className="border border-ink-300/20 px-2 py-1.5 align-bottom text-right">Day<br />Fat %</th>
                  <th rowSpan={2} className="border border-ink-300/20 px-2 py-1.5 align-bottom text-right">Cumulative<br />Quantity</th>
                  <th rowSpan={2} className="border border-ink-300/20 px-2 py-1.5 align-bottom text-right">Cumulative<br />Amount</th>
                </tr>
                <tr>
                  {(showThird ? [...SHIFT_GROUPS, { key: 'third' as const, label: 'Third', bg: '', barColor: '' }] : SHIFT_GROUPS).map((g) => (
                    <React.Fragment key={g.key}>
                      <th className={`border border-ink-300/20 px-2 py-1 text-right text-helper-text ${g.bg}`}>Cows</th>
                      <th className={`border border-ink-300/20 px-2 py-1 text-right text-helper-text ${g.bg}`}>Litres</th>
                      <th className={`border border-ink-300/20 px-2 py-1 text-right text-helper-text ${g.bg}`}>Fat %</th>
                      <th className={`border border-ink-300/20 px-2 py-1 text-right text-helper-text ${g.bg}`}>Fare</th>
                      <th className={`border border-ink-300/20 px-2 py-1 text-right text-helper-text ${g.bg}`}>Total fare</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chartDays.map((d) => (
                  <tr key={d.production_date}>
                    <td className="border border-ink-300/20 px-2 py-1.5">{formatDate(d.production_date)}</td>
                    {(showThird ? [d.morning, d.evening, d.third] : [d.morning, d.evening]).map((shift, i) => (
                      <React.Fragment key={i}>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{shift.cows ? shift.cows : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{shift.litres ? formatNumber(shift.litres, 1) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{shift.fat_percent != null ? formatNumber(shift.fat_percent, 1) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{shift.rate_per_litre != null ? formatNumber(shift.rate_per_litre, 2) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{shift.amount ? formatCurrency(shift.amount) : '—'}</td>
                      </React.Fragment>
                    ))}
                    <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-semibold">{d.cows ? d.cows : '—'}</td>
                    <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-semibold">{d.day_fat_percent != null ? formatNumber(d.day_fat_percent, 2) : '—'}</td>
                    <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-medium">{formatNumber(d.cumulative_quantity, 1)}</td>
                    <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-medium">{formatCurrency(d.cumulative_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ILMSCard>
  );
}

// Sold vs consumed are two identities on the litres chart - blue/gold
// tokens from frontend/tailwind.config.js, a pair that stays distinct under
// colour-blindness (green/gold doesn't). Amount has its own chart (one
// y-scale per chart, never a dual axis) in brand green.

const REPORT_GROUPS: { id: MilkReportGroup; label: string; consumption: boolean }[] = [
  { id: 'Partner', label: 'Partner', consumption: false },
  { id: 'Vendor', label: 'Vendor', consumption: false },
  { id: 'Customer', label: 'Customer', consumption: false },
  { id: 'CalfConsumption', label: 'Consumption for Calves', consumption: true },
  { id: 'OfficeConsumption', label: 'Consumption for Office', consumption: true },
];
const ALL_GROUP_IDS = REPORT_GROUPS.map((g) => g.id);

function MilkSalesByCustomerReport() {
  const [startDate, setStartDate] = useState(daysAgo(29));
  const [endDate, setEndDate] = useState(today());
  const [groups, setGroups] = useState<MilkReportGroup[]>(ALL_GROUP_IDS);
  const [calendarMonth, setCalendarMonth] = useState(today().slice(0, 7));
  const [calendarMetric, setCalendarMetric] = useState<'litres' | 'amount'>('litres');

  const toggleGroup = (id: MilkReportGroup) => setGroups((prev) => (
    prev.includes(id) ? prev.filter((g) => g !== id) : ALL_GROUP_IDS.filter((g) => g === id || prev.includes(g))
  ));
  const allSelected = groups.length === ALL_GROUP_IDS.length;

  const reportQuery = useQuery({
    queryKey: ['report-milk-sales-by-customer', startDate, endDate, groups, calendarMonth],
    queryFn: () => reportsApi.milkSalesByCustomer({ startDate, endDate, groups, month: calendarMonth }),
    enabled: groups.length > 0,
  });
  const report = groups.length > 0 ? reportQuery.data : undefined;
  const parties = report?.customers ?? [];
  // Calf/Office consumption rows have no buyer - litres only, ₹0.
  const sold = parties.filter((c) => c.party_type !== 'Consumption');
  const consumed = parties.filter((c) => c.party_type === 'Consumption');
  const soldLitres = sold.reduce((t, c) => t + c.total_litres, 0);
  const consumedLitres = consumed.reduce((t, c) => t + c.total_litres, 0);
  // One bar per selected category (not per person) - the By Customer table
  // below keeps the per-person detail.
  const groupChartData = REPORT_GROUPS.filter((g) => groups.includes(g.id)).map((g) => {
    const rows = parties.filter((c) => c.group === g.id);
    return {
      label: g.label, consumption: g.consumption,
      total_litres: rows.reduce((t, c) => t + c.total_litres, 0),
      total_amount: rows.reduce((t, c) => t + c.total_amount, 0),
    };
  });
  const tableMonths = report ? trimLeadingEmpty(report.months) : [];

  return (
    <ILMSCard
      title="Milk Sales by Customer"
      subtitle="Litres and amount (₹) sold to each Partner/Vendor/Customer, plus milk consumed by calves and the office (litres only, ₹0) — only Approved entries count."
    >
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <ILMSDatePicker label="From" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        <ILMSDatePicker label="To" value={endDate} max={today()} onChange={(e) => setEndDate(e.target.value)} />
        <div className="flex flex-col gap-1">
          <span className="text-label text-ink-700">Category</span>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Category">
            <button
              type="button" aria-pressed={allSelected}
              onClick={() => setGroups(allSelected ? [] : ALL_GROUP_IDS)}
              className={`rounded-full border px-3 h-9 text-helper-text font-medium ${allSelected ? 'border-brand-green-600 bg-brand-green-600 text-white' : 'border-ink-300/40 text-ink-600 hover:border-brand-green-400'}`}
            >
              All
            </button>
            {REPORT_GROUPS.map((g) => {
              const on = groups.includes(g.id);
              return (
                <button
                  key={g.id} type="button" aria-pressed={on} onClick={() => toggleGroup(g.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 h-9 text-helper-text font-medium ${on ? 'border-brand-green-600 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/40 text-ink-500 hover:border-brand-green-400'}`}
                >
                  <span aria-hidden="true">{on ? '✓' : '+'}</span>{g.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-1.5 pb-0.5">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => { setEndDate(today()); setStartDate(daysAgo(p.days - 1)); }}
              className="rounded-md border border-ink-300/30 px-2.5 h-9 text-helper-text text-ink-600 hover:border-brand-green-400 hover:text-brand-green-700"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 && <p className="text-secondary text-ink-400 py-10 text-center">Select at least one category.</p>}
      {reportQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(reportQuery.error)}</ILMSAlert>}
      {groups.length > 0 && reportQuery.isLoading && <ILMSCardSkeleton count={1} />}

      {report && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <ILMSKPI label="Milk Sold (L)" value={`${formatNumber(soldLitres, 1)} L`} icon={<Milk />} tone="success" />
            <ILMSKPI label="Total Amount (₹)" value={formatCurrency(report.total_amount)} icon={<IndianRupee />} tone="info" />
            <ILMSKPI label="Consumed — Calves + Office (L)" value={`${formatNumber(consumedLitres, 1)} L`} icon={<Milk />} tone="warning"
              helperText={consumed.map((c) => `${c.customer_name.replace('Consumption for ', '')} ${formatNumber(c.total_litres, 1)} L`).join(' · ') || 'None in this range'} />
            <ILMSKPI label="Customers" value={String(sold.length)} icon={<Users />} tone="neutral" />
          </div>

          {report.customers.length === 0 ? (
            <p className="text-secondary text-ink-400 py-10 text-center">No approved milk sales or consumption in this range.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div>
                  <SmartChart id="reports-sales-litres" data={groupChartData} />
                </div>
                <div>
                  {groupChartData.every((g) => g.consumption) ? (
                    <>
                      <p className="text-card-title mb-1">Amount (₹)</p>
                      <p className="text-secondary text-ink-400 py-10 text-center">Consumption carries no amount — select Partner, Vendor or Customer.</p>
                    </>
                  ) : (
                    <SmartChart id="reports-sales-amount" data={groupChartData} />
                  )}
                </div>
              </div>

              {tableMonths.length > 1 && (
                <div className="mt-6">
                  <p className="text-card-title mb-2">Monthly Summary</p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-table-text">
                      <thead>
                        <tr>
                          <th className="border border-ink-300/20 px-2 py-1.5 text-left">Month</th>
                          <th className="border border-ink-300/20 px-2 py-1.5 text-right">Sales</th>
                          <th className="border border-ink-300/20 px-2 py-1.5 text-right">Litres</th>
                          <th className="border border-ink-300/20 px-2 py-1.5 text-right">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableMonths.map((m) => (
                          <tr key={m.month}>
                            <td className="border border-ink-300/20 px-2 py-1.5">{monthLabel(m.month)}</td>
                            <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{m.sale_count}</td>
                            <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{formatNumber(m.total_litres, 1)}</td>
                            <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-medium">{formatCurrency(m.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 overflow-x-auto">
                <p className="text-card-title mb-2">By Customer</p>
                <table className="w-full border-collapse text-table-text">
                  <thead>
                    <tr>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-left">Customer</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-left">Type</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Sales</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Litres</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Avg Rate (₹/L)</th>
                      <th className="border border-ink-300/20 px-2 py-1.5 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.customers.map((c) => (
                      <tr key={c.customer_id}>
                        <td className="border border-ink-300/20 px-2 py-1.5">{c.customer_name}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5">{c.party_type}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{c.sale_count}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{formatNumber(c.total_litres, 1)}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums">{c.avg_rate_per_litre != null ? formatNumber(c.avg_rate_per_litre, 2) : '—'}</td>
                        <td className="border border-ink-300/20 px-2 py-1.5 text-right tabular-nums font-medium">{formatCurrency(c.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Independent of the By Customer section above (which is empty
              when the From/To range has no sales) - this always uses its
              own month filter, so it can show a past/future month's data
              even when the top summary range is empty. */}
          <div className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
              <p className="text-card-title">Daily Calendar — {monthLabel(report.calendar_month)}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-md border border-ink-300/30 overflow-hidden">
                      <button
                        type="button" onClick={() => setCalendarMetric('litres')}
                        className={cn('px-3 h-8 text-helper-text', calendarMetric === 'litres' ? 'bg-brand-green-600 text-white' : 'text-ink-600 hover:bg-surface-muted')}
                      >
                        Litres
                      </button>
                      <button
                        type="button" onClick={() => setCalendarMetric('amount')}
                        className={cn('px-3 h-8 text-helper-text border-l border-ink-300/30', calendarMetric === 'amount' ? 'bg-brand-green-600 text-white' : 'text-ink-600 hover:bg-surface-muted')}
                      >
                        Amount (₹)
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button" onClick={() => setCalendarMonth((m) => shiftMonth(m, -1))} aria-label="Previous month"
                        className="h-8 w-8 rounded-md border border-ink-300/30 text-ink-600 hover:border-brand-green-400 hover:text-brand-green-700"
                      >
                        ‹
                      </button>
                      <button
                        type="button" onClick={() => setCalendarMonth((m) => shiftMonth(m, 1))} aria-label="Next month"
                        className="h-8 w-8 rounded-md border border-ink-300/30 text-ink-600 hover:border-brand-green-400 hover:text-brand-green-700"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-helper-text text-ink-500 mb-3">
                  Each cell is one day's {calendarMetric === 'litres' ? 'litres' : 'amount'} for that customer — hover a day for the Morning/Evening split.
                </p>

                {report.calendar_rows.length === 0 ? (
                  <p className="text-secondary text-ink-400 py-6 text-center">No approved milk sales in {monthLabel(report.calendar_month)}.</p>
                ) : (
                  // table-fixed + 100% width makes every column share out the
                  // card's own width instead of sizing to its content — the
                  // only way a month's worth of day columns (up to 31) plus
                  // Customer/Type/Totals can all fit with no horizontal (or,
                  // since nothing here is height-capped, vertical) scrollbar.
                  <table className="w-full table-fixed border-collapse text-table-text">
                    <colgroup>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '5%' }} />
                      {report.calendar_dates.map((d) => <col key={d} />)}
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '8%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="border border-ink-300/20 px-1 py-1 text-left text-[10px]">Customer</th>
                        <th className="border border-ink-300/20 px-0.5 py-1 text-center text-[10px]">Type</th>
                        {report.calendar_dates.map((d) => {
                          const dt = new Date(`${d}T00:00:00`);
                          return (
                            <th key={d} className="border border-ink-300/20 px-0 py-1 text-center font-medium">
                              <div className="flex flex-col items-center leading-none">
                                <span className="text-[7px] text-ink-400">{dt.toLocaleDateString('en-IN', { weekday: 'narrow' })}</span>
                                <span className="text-[9px]">{dt.getDate()}</span>
                              </div>
                            </th>
                          );
                        })}
                        <th className="border border-ink-300/20 px-1 py-1 text-right text-[10px]">Total L</th>
                        <th className="border border-ink-300/20 px-1 py-1 text-right text-[10px]">Total ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.calendar_rows.map((row) => {
                        const rowMax = Math.max(...row.days.map((d) => (calendarMetric === 'litres' ? d.litres : d.amount)), 0.0001);
                        return (
                          <tr key={row.customer_id}>
                            <td className="border border-ink-300/20 px-1 py-1 text-[10px] font-medium overflow-hidden text-ellipsis whitespace-nowrap" title={row.customer_name}>
                              {row.customer_name}
                            </td>
                            <td className="border border-ink-300/20 px-0.5 py-1 text-center text-[9px]" title={row.party_type}>
                              {row.party_type.charAt(0)}
                            </td>
                            {row.days.map((d, idx) => {
                              const value = calendarMetric === 'litres' ? d.litres : d.amount;
                              const intensity = value / rowMax;
                              const nearRightEdge = idx > report.calendar_dates.length - 6;
                              return (
                                <td key={d.date} className="group relative border border-ink-300/20 p-0 text-center">
                                  <div
                                    className="flex h-7 items-center justify-center overflow-hidden text-[9px] font-medium tabular-nums leading-none"
                                    style={value > 0 ? { backgroundColor: `rgba(47, 99, 53, ${0.15 + intensity * 0.6})`, color: intensity > 0.5 ? '#fff' : '#1f2a1f' } : undefined}
                                  >
                                    {value > 0 ? formatNumber(value, calendarMetric === 'litres' ? 1 : 0) : <span className="text-ink-300">–</span>}
                                  </div>
                                  {d.shifts.length > 0 && (
                                    <div className={cn(
                                      'pointer-events-none absolute top-full z-20 mt-1 hidden w-max rounded-md border border-ink-300/20 bg-surface px-2.5 py-2 text-left shadow-popover group-hover:block',
                                      nearRightEdge ? 'right-0' : 'left-1/2 -translate-x-1/2',
                                    )}>
                                      <p className="mb-1 text-[11px] font-semibold text-ink-700">{formatDate(d.date)}</p>
                                      {d.shifts.map((s) => (
                                        <p key={s.shift} className="text-[11px] text-ink-600 whitespace-nowrap">
                                          {s.shift}: {formatNumber(s.litres, 2)}L — {formatCurrency(s.amount)}
                                        </p>
                                      ))}
                                      <p className="mt-1 border-t border-ink-300/20 pt-1 text-[11px] font-medium text-ink-800 whitespace-nowrap">
                                        Total: {formatNumber(d.litres, 2)}L — {formatCurrency(d.amount)}
                                      </p>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="border border-ink-300/20 px-1 py-1 text-right text-[10px] tabular-nums">{formatNumber(row.total_litres, 1)}</td>
                            <td className="border border-ink-300/20 px-1 py-1 text-right text-[10px] tabular-nums font-medium">{formatCurrency(row.total_amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
        </>
      )}
    </ILMSCard>
  );
}

export default function ReportsPage() {
  return (
    <TabbedPage
      title="Operational Reports"
      subtitle="Herd, health, production and utility reports for management review."
      tabs={[
        { id: 'milk-production', label: 'Milk Production', render: () => <DailyMilkProductionReport /> },
        { id: 'milk-sales', label: 'Milk Sales by Customer', render: () => <MilkSalesByCustomerReport /> },
      ]}
    />
  );
}
